import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { submitCommissionLead } from "../api/submitCommissionLead";
import {
  backendProjectType,
  commissionProjectChoices,
  type CommissionProjectChoiceId,
} from "../data/commissionOptions";
import {
  parseInquireArtworkSearch,
  projectChoiceForMedium,
} from "../lib/inquireArtwork";

type FormState = {
  senderName: string;
  senderEmail: string;
  senderPhone: string;
  projectChoice: CommissionProjectChoiceId | "";
  message: string;
  website: string;
};

const initialState: FormState = {
  senderName: "",
  senderEmail: "",
  senderPhone: "",
  projectChoice: "",
  message: "",
  website: "",
};

export function CommissionForm() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>(initialState);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const inquireArtwork = parseInquireArtworkSearch(searchParams.toString());

  useEffect(() => {
    document.title = t("contact.pageTitle");
    return () => {
      document.title = t("common.siteName");
    };
  }, [t]);

  useEffect(() => {
    if (!inquireArtwork?.name || !inquireArtwork.medium) return;
    const { name, medium } = inquireArtwork;
    setForm((prev) => {
      if (prev.message.trim()) return prev;
      return {
        ...prev,
        message: t("contact.form.artworkPrefill", {
          title: name,
          medium: t(`home.featured.mediums.${medium}`),
        }),
        projectChoice: projectChoiceForMedium(medium),
      };
    });
  }, [inquireArtwork, t]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (status === "error") {
      setStatus("idle");
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    const result = await submitCommissionLead({
      senderName: form.senderName,
      senderEmail: form.senderEmail,
      senderPhone: form.senderPhone || undefined,
      projectType: backendProjectType(form.projectChoice),
      message: form.message.trim() || t("contact.form.messageFallback"),
      locale: i18n.language,
      sourceUrl: typeof window !== "undefined" ? window.location.href : undefined,
      referrerPath:
        typeof document !== "undefined" ? document.referrer || undefined : undefined,
      website: form.website,
    });

    if (!result.ok) {
      setStatus("error");
      setErrorMessage(result.error);
      return;
    }

    setStatus("success");
    setForm(initialState);
  };

  if (status === "success") {
    return (
      <div className="commission-success animate-fade-in py-4 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <h3 className="mt-4 font-display text-xl font-semibold text-canvas-950">
          {t("contact.form.successTitle")}
        </h3>
        <p className="mt-2 text-sm text-canvas-600">{t("contact.form.successBody")}</p>
        <a
          href="https://www.instagram.com/shanir_arts"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-accent mt-5 inline-flex !px-5 !py-2 !text-xs"
        >
          {t("contact.form.successInstagram")}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="commission-form" noValidate>
      {inquireArtwork?.name && (
        <div className="mb-5 rounded-xl bg-accent/8 px-4 py-3 ring-1 ring-accent/20">
          <p className="text-xs font-medium uppercase tracking-[0.1em] text-accent">
            {t("contact.form.artworkInquiryLabel")}
          </p>
          <p className="mt-1 font-display text-base font-medium text-canvas-950">
            {inquireArtwork.name}
          </p>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="commission-name" className="form-label">
            {t("contact.form.name")}
          </label>
          <input
            id="commission-name"
            type="text"
            required
            autoComplete="name"
            className="form-input"
            placeholder={t("contact.form.namePlaceholder")}
            value={form.senderName}
            onChange={(e) => update("senderName", e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="commission-email" className="form-label">
            {t("contact.form.email")}
          </label>
          <input
            id="commission-email"
            type="email"
            required
            autoComplete="email"
            className="form-input"
            placeholder={t("contact.form.emailPlaceholder")}
            value={form.senderEmail}
            onChange={(e) => update("senderEmail", e.target.value)}
          />
        </div>
      </div>

      <div className="mt-3">
        <label htmlFor="commission-phone" className="form-label">
          {t("contact.form.phone")}
        </label>
        <input
          id="commission-phone"
          type="tel"
          autoComplete="tel"
          className="form-input"
          placeholder={t("contact.form.phonePlaceholder")}
          value={form.senderPhone}
          onChange={(e) => update("senderPhone", e.target.value)}
        />
      </div>

      <div className="mt-5">
        <p className="form-label mb-2">{t("contact.form.projectTypeLabel")}</p>
        <div className="flex flex-wrap gap-2">
          {commissionProjectChoices.map(({ id }) => {
            const selected = form.projectChoice === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => update("projectChoice", selected ? "" : id)}
                className={`commission-pill ${selected ? "commission-pill--selected" : ""}`}
                aria-pressed={selected}
              >
                {t(`contact.form.projectChoices.${id}`)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="commission-message" className="form-label">
          {t("contact.form.message")}
        </label>
        <textarea
          id="commission-message"
          rows={2}
          className="form-input form-textarea"
          placeholder={t("contact.form.messagePlaceholder")}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
        />
      </div>

      <div className="sr-only" aria-hidden>
        <label htmlFor="commission-website">Website</label>
        <input
          id="commission-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => update("website", e.target.value)}
        />
      </div>

      {status === "error" && errorMessage && (
        <p
          className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200"
          role="alert"
        >
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="btn-accent mt-5 w-full disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "submitting" ? t("contact.form.submitting") : t("contact.form.submit")}
      </button>

      <p className="mt-3 text-center text-xs text-canvas-500">
        {t("contact.form.footer")}
      </p>
    </form>
  );
}
